import { TaskEditButton } from './TaskEditButton';
import { parseRecurrence } from '@horizon/shared';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Check, Clock, CornerUpRight } from 'lucide-react';
import type { Task } from '@horizon/shared';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface TaskRowProps {
  task: Task;
  today: string;
  selected?: boolean;
  /** В инбоксе перенос делают явные кнопки дат, дублировать его стрелкой незачем. */
  showDefer?: boolean;
  onToggle: (task: Task) => void;
  onDefer: (task: Task) => void;
}

const SWIPE_THRESHOLD = 72;

/**
 * Свайп — ускорение, а не единственный способ: выполнение и перенос
 * доступны кнопками и с клавиатуры (ROADMAP, сквозные требования).
 */
export function TaskRow({
  task,
  today,
  selected,
  showDefer = true,
  onToggle,
  onDefer,
}: TaskRowProps) {
  const { t, i18n } = useTranslation();
  const [offset, setOffset] = useState(0);
  const start = useRef<number | null>(null);

  const done = task.status === 'done';
  const overdue = !!task.dueDate && task.dueDate < today && !done;

  const endSwipe = () => {
    if (offset > SWIPE_THRESHOLD) onToggle(task);
    else if (offset < -SWIPE_THRESHOLD) onDefer(task);
    setOffset(0);
    start.current = null;
  };

  return (
    <li
      className="task-motion relative overflow-hidden rounded-card border border-line bg-surface"
      data-selected={selected || undefined}
      style={offset !== 0 ? { transform: `translateX(${offset}px)` } : undefined}
      onPointerDown={(e) => {
        if (e.pointerType === 'mouse') return;
        start.current = e.clientX;
      }}
      onPointerMove={(e) => {
        if (start.current === null) return;
        setOffset(Math.max(-120, Math.min(120, e.clientX - start.current)));
      }}
      onPointerUp={endSwipe}
      onPointerCancel={endSwipe}
    >
      <div
        className={cn(
          'flex items-start gap-3 px-3 py-3',
          selected && 'ring-2 ring-accent ring-inset rounded-card',
        )}
      >
        <Button
          size="icon"
          variant="ghost"
          role="checkbox"
          aria-checked={done}
          aria-label={
            done
              ? t('task.markTodo', { title: task.title })
              : t('task.markDone', { title: task.title })
          }
          onClick={() => onToggle(task)}
          className="shrink-0"
        >
          <span
            aria-hidden="true"
            className={cn(
              'flex size-5 items-center justify-center rounded-md border',
              done ? 'border-accent bg-accent text-accent-fg' : 'border-line',
            )}
          >
            {done && <Check className="size-3.5" strokeWidth={3} />}
          </span>
        </Button>

        <div className="min-w-0 flex-1 py-1.5">
          <p className={cn('break-words text-[15px]', done && 'text-muted line-through')}>
            {task.title}
          </p>
          {task.priority !== 'none' && (
            <p className="text-xs text-muted">{t(`editor.priorities.${task.priority}`)}</p>
          )}
          {task.recurrenceRule && (
            <p className="text-xs text-muted">
              {t(parseRecurrence(task.recurrenceRule)?.until ? 'editor.finite' : 'editor.routine')}
            </p>
          )}
          {task.goal && (
            <p className="mt-1 text-xs">
              <span
                className="inline-flex items-center gap-1 rounded-md bg-surface-2 px-1.5 py-0.5 text-far"
                style={task.goal.color ? { color: task.goal.color } : undefined}
              >
                {task.goal.emoji && <span aria-hidden="true">{task.goal.emoji}</span>}
                {task.goal.title}
              </span>
            </p>
          )}
          {(task.dueDate || task.estimateMinutes || task.tags.length > 0) && (
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
              {task.dueDate && (
                <span className={cn('inline-flex items-center gap-1', overdue && 'text-near')}>
                  <CalendarClock aria-hidden="true" className="size-3.5" />
                  {t('task.due', { date: formatDate(task.dueDate, i18n.language) })}
                </span>
              )}
              {task.estimateMinutes != null && (
                <span className="inline-flex items-center gap-1">
                  <Clock aria-hidden="true" className="size-3.5" />
                  {t('task.minutes', { count: task.estimateMinutes })}
                </span>
              )}
              {task.tags.map((tag) => (
                <span key={tag} className="rounded-md bg-surface-2 px-1.5 py-0.5">
                  #{tag}
                </span>
              ))}
            </p>
          )}
        </div>

        <TaskEditButton task={task} />
        {!done && showDefer && (
          <Button
            size="icon"
            variant="ghost"
            aria-label={t('task.deferOne', { title: task.title })}
            onClick={() => onDefer(task)}
            className="shrink-0"
          >
            <CornerUpRight aria-hidden="true" className="size-4" />
          </Button>
        )}
      </div>
    </li>
  );
}

function formatDate(date: string, locale: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}
